import { bounds as boundsOps, type Bounds } from '../geometry';
import { type Vec2 } from '../math';

import { type Evaluated } from './evaluated';

/**
 * The world rectangle a value occupies.
 *
 * ## Why it returns `null` rather than an infinite rectangle
 *
 * A line and a ray genuinely have no bounds. Returning something enormous instead would be
 * a lie that spreads: an export framed on the content would zoom out to nothing, and a
 * "fit to content" command would put every real object in one pixel. `null` says *this has
 * no extent to frame on*, and callers decide — the exporters below frame on the bounded
 * objects and then clip the unbounded ones to that frame, which is what a reader expects a
 * drawing of a line to look like.
 *
 * ## Labels are points
 *
 * Text and measurements return the bounds of their anchor, not of their painted glyphs.
 * Measuring text needs a canvas and a font, neither of which belongs in the domain. The
 * consequence is that a label near the edge of a drawing can extend past an exported frame;
 * the exporters therefore add a margin rather than framing tightly.
 */
export function boundsOfValue(value: Evaluated): Bounds | null {
  switch (value.kind) {
    case 'point':
      return boundsOps.fromPoint(value.point);

    case 'segment':
      return boundsOps.ofSegment(value.segment);

    case 'vector':
      return boundsOps.ofSegment({ a: value.from, b: value.to });

    case 'circle':
      return boundsOps.ofCircle(value.circle);

    case 'arc':
      return boundsOps.ofArc(value.arc);

    case 'ellipse':
      return boundsOps.ofEllipse(value.ellipse);

    case 'polygon':
      return boundsOps.ofPolygon(value.vertices);

    case 'polyline':
      return value.points.length === 0 ? null : boundsOps.fromPoints(value.points);

    /*
     * The union of every drawn piece.
     *
     * Bounded even though the function is not: what is bounded is what was *plotted*, over the
     * range the graph carries. That is the honest answer for framing an export — a page cannot
     * be fitted to `y = x^2` in general, only to the part of it that is on the paper.
     */
    // The axes themselves; the numbers beside them are text, which is bounded by its anchor
    // everywhere else in this file too.
    case 'axes':
      return boundsOps.fromPoints([value.xAxis.a, value.xAxis.b, value.yAxis.a, value.yAxis.b]);

    // The frame, which encloses every rule and every number by construction.
    /*
     * The dots and their guides, and not the text.
     *
     * A label's extent is a matter of font metrics, which this layer has no business knowing —
     * the same reason `text` and `measurement` contribute nothing here. What that costs is a
     * coordinate clipped at the edge of a fitted export, and what it buys is a domain that does
     * not depend on how anything is drawn.
     */
    case 'graph-marks': {
      const points = [...value.points, ...value.rules.flatMap((rule) => [rule.a, rule.b])];
      return points.length === 0 ? null : boundsOps.fromPoints(points);
    }

    case 'variation-table':
      return boundsOps.bounds(
        value.corner.x,
        value.corner.y - value.height,
        value.corner.x + value.width,
        value.corner.y
      );

    case 'function-graph': {
      const points = value.segments.flat();
      return points.length === 0 ? null : boundsOps.fromPoints(points);
    }

    // A viewpoint occupies no part of the page, so it contributes nothing to what an export
    // has to fit around. `null` is what every other value with nothing drawn returns.
    case 'space':
      return null;

    case 'solid': {
      const points: Vec2[] = [];
      for (const edge of [...value.visible, ...value.hidden]) points.push(edge.a, edge.b);
      return points.length === 0 ? null : boundsOps.fromPoints(points);
    }

    case 'image':
      return boundsOps.bounds(
        value.position.x,
        value.position.y,
        value.position.x + value.width,
        value.position.y + value.height
      );

    /*
     * The vertex alone. The mark is drawn at a size fixed in *screen* pixels, so its extent
     * in world units depends on the zoom — a frame computed from it would change with the
     * camera, which is not something a document's bounds may do.
     */
    case 'angle-mark':
      return boundsOps.fromPoint(value.vertex);

    case 'text':
    case 'measurement':
      // The anchor, not the glyphs. See the note above.
      return boundsOps.fromPoint(value.position);

    /*
     * Unbounded by definition. Not an oversight and not a degenerate case: a line really
     * does extend forever, and saying so is more useful than inventing a number.
     */
    case 'line':
    case 'ray':
      return null;
  }
}

/** True when the value extends forever and can only be drawn clipped to a frame. */
export function isUnbounded(value: Evaluated): boolean {
  return value.kind === 'line' || value.kind === 'ray';
}

/**
 * The rectangle enclosing every bounded value.
 *
 * Returns `null` when nothing has any extent — an empty document, or one holding only
 * lines. A caller framing a view then has to choose a default rather than being handed a
 * zero-sized rectangle it would have to detect.
 */
export function boundsOfValues(values: Iterable<Evaluated>): Bounds | null {
  let box: Bounds | null = null;
  for (const value of values) {
    const own = boundsOfValue(value);
    if (own === null) continue;
    box = box === null ? own : boundsOps.union(box, own);
  }
  return box;
}
